import { useState } from "react";
import { NumberField } from "../NumberField";
import { FORMATION_LIST } from "../formations";
import { VIS_STYLE_LIST } from "../source";
import type { useSwarmSource } from "../hooks/useSwarmSource";
import type { usePhysics } from "../hooks/usePhysics";
import type { useShowLoader } from "../hooks/useShowLoader";
import type { useAudio } from "../hooks/useAudio";
import { AudioControls } from "./AudioControls";
import { GradientEditor } from "./GradientEditor";
import { Section } from "./Section";

// quick palette (white + July-4th red/white/blue + a few show favourites)
const COLOR_PRESETS = ["#ffffff", "#ff2233", "#2a6cff", "#ffcf40", "#23d18b", "#ff3ea5", "#35e0ff"];

type Props = {
  src: ReturnType<typeof useSwarmSource>;
  physics: ReturnType<typeof usePhysics>;
  showLoader: ReturnType<typeof useShowLoader>;
  bloom: boolean;
  setBloom: React.Dispatch<React.SetStateAction<boolean>>;
  bloomStrength: number;
  setBloomStrength: React.Dispatch<React.SetStateAction<number>>;
  brightness: number;
  setBrightness: React.Dispatch<React.SetStateAction<number>>;
  rotate: boolean;
  setRotate: React.Dispatch<React.SetStateAction<boolean>>;
  rotateSpeed: number;
  setRotateSpeed: React.Dispatch<React.SetStateAction<number>>;
  constrainView: boolean;
  setConstrainView: React.Dispatch<React.SetStateAction<boolean>>;
  audio: ReturnType<typeof useAudio>;
  error: string;
};

/** The panel shown in studio mode: pick a source, render mode, physics, tunables. */
export function SourceControls({
  src, physics, showLoader, bloom, setBloom, bloomStrength, setBloomStrength,
  brightness, setBrightness, rotate, setRotate, rotateSpeed, setRotateSpeed,
  constrainView, setConstrainView, audio, error,
}: Props) {
  const {
    source, count, setCount, sourceScale, setSourceScale, textInput, setTextInput,
    font, fontList, renderMode, setRenderMode, colorMode, setColorMode, solidColor, pickSolid,
    gradient, setGradient, gradientAxis, setGradientAxis,
    pickFormation, spell, onFontChange, loadSystemFonts, onSvgFile, onImgFile,
    liss, applyLissajous, setLissParam,
    flowOn, toggleFlow, flowSpeed, setFlowSpeed,
    applyVisualizer, visStyle,
    cym, cymAmp, setCymAmp, applyCymatics, setCymParam, ladderLength,
    sweepOn, toggleSweep, sweepRate, setSweepRate, sweepLo, setSweepLo, sweepHi, setSweepHi,
    audioSweepOn, toggleAudioSweep, energySweepOn, toggleEnergySweep,
  } = src;
  const { mode: physMode, setMode: setPhysMode, params: phys, setParam: pd } = physics;
  const { loading, loadSample, onShowFile } = showLoader;

  const [openSwarm, setOpenSwarm] = useState(false);
  const [openShapes, setOpenShapes] = useState(false);
  const [openLiss, setOpenLiss] = useState(false);
  const [openCym, setOpenCym] = useState(false);
  const [openVis, setOpenVis] = useState(false);
  const [openText, setOpenText] = useState(false);
  const [openColor, setOpenColor] = useState(false);
  const [openAudio, setOpenAudio] = useState(false);

  return (
    <>
      <Section
        title="Swarm"
        summary={`${physMode === "neighbor" ? "Neighbor" : "Base"} · ${count.toLocaleString()}`}
        open={openSwarm}
        onToggle={() => setOpenSwarm((o) => !o)}
      >
        <div className="panel-row physics-row">
          <span className="seg">
            <button className={"chip seg-btn" + (physMode === "base" ? " active" : "")} onClick={() => setPhysMode("base")}>Base physics</button>
            <button className={"chip seg-btn" + (physMode === "neighbor" ? " active" : "")} onClick={() => setPhysMode("neighbor")}>Neighbor physics</button>
          </span>
          {physMode === "neighbor" && <span className="phys-note">drones repel inside their safe zone — no two touch</span>}
        </div>
        {physMode === "neighbor" && (
          <div className="panel-row dials">
            <NumberField label="Safe zone" value={phys.safeZone} min={0.1} max={10} step={0.1} onChange={pd("safeZone")} format={(v) => v.toFixed(1) + " lu"} />
            <NumberField label="Speed" value={phys.maxSpeed} min={1} max={1000} step={1} onChange={pd("maxSpeed")} format={(v) => v + " lu/s"} />
            <NumberField label="Rigidity" value={phys.rigidity} min={0.5} max={30} step={0.5} onChange={pd("rigidity")} />
            <NumberField label="Avoidance" value={phys.avoidance} min={0} max={80} step={1} onChange={pd("avoidance")} />
            <NumberField label="Damping" value={phys.damping} min={0.5} max={10} step={0.1} onChange={pd("damping")} format={(v) => v.toFixed(1)} />
            <span className="phys-note">1 lu ≈ 0.1 m — safe 10 ≈ 1 m apart · speed 100 ≈ 10 m/s</span>
          </div>
        )}
        <div className="panel-row dials">
          <NumberField label="Drones" value={count} min={200} max={16000} step={100} onChange={(v) => setCount(v)} format={(v) => v.toLocaleString()} />
          <NumberField label="Size" value={sourceScale} min={0.3} max={3} step={0.1} onChange={(v) => setSourceScale(v)} format={(v) => v.toFixed(1) + "×"} />
        </div>
        <div className="panel-row cym-row">
          <button className={"chip toggle" + (rotate ? " active" : "")} onClick={() => setRotate((r) => !r)}>{rotate ? "⟳ Rotation on" : "⟳ Rotation off"}</button>
          {rotate && !constrainView && <NumberField label="Spin" value={rotateSpeed} min={0.5} max={24} step={0.5} onChange={setRotateSpeed} format={(v) => v.toFixed(1) + "°/s"} />}
          <button
            className={"chip toggle" + (constrainView ? " active" : "")}
            onClick={() => setConstrainView((c) => !c)}
            title="Auto-frame the swarm: straight-on view that smoothly zooms out/in as the swarm grows or shrinks (overrides orbit + rotation while on)"
          >
            {constrainView ? "▣ Constrained" : "▣ Constrain view"}
          </button>
        </div>
      </Section>

      <Section
        title="Shapes"
        summary={source.kind === "formation" ? FORMATION_LIST.find((f) => f.key === source.key)?.label : undefined}
        open={openShapes}
        onToggle={() => setOpenShapes((o) => !o)}
      >
        <div className="panel-row formations">
          {FORMATION_LIST.map((f) => (
            <button
              key={f.key}
              className={"chip" + (source.kind === "formation" && source.key === f.key ? " active" : "")}
              onClick={() => pickFormation(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </Section>

      <Section
        title="Lissajous"
        summary={source.kind === "lissajous" ? `${liss.a}:${liss.b}:${liss.c}${flowOn ? " ≈" : ""}` : undefined}
        open={openLiss}
        onToggle={() => {
          const opening = !openLiss;
          setOpenLiss(opening);
          if (opening && source.kind !== "lissajous") applyLissajous();
        }}
      >
        <div className="panel-row dials liss-dials">
          <NumberField label="a (x)" value={liss.a} min={1} max={12} step={1} onChange={setLissParam("a")} />
          <NumberField label="b (y)" value={liss.b} min={1} max={12} step={1} onChange={setLissParam("b")} />
          <NumberField label="c (z)" value={liss.c} min={1} max={12} step={1} onChange={setLissParam("c")} />
          <NumberField label="Phase" value={liss.phase} min={0} max={360} step={5} onChange={setLissParam("phase")} format={(v) => v + "°"} />
          <NumberField label="Depth" value={liss.depth} min={0} max={1} step={0.05} onChange={setLissParam("depth")} format={(v) => v.toFixed(2)} />
          {source.kind !== "lissajous" && (
            <button className="chip" onClick={applyLissajous}>✦ Show curve</button>
          )}
        </div>
        <div className="panel-row cym-row">
          <button className={"chip toggle" + (flowOn ? " active" : "")} onClick={toggleFlow} title="The swarm streams around the curve (neighbor physics)">
            {flowOn ? "■ Flow" : "≈ Flow"}
          </button>
          {flowOn && (
            <NumberField label="Flow" value={flowSpeed} min={0.5} max={30} step={0.5} onChange={setFlowSpeed} format={(v) => v.toFixed(1) + " laps/min"} />
          )}
        </div>
        <span className="phys-note">a native parametric curve — physics &amp; music act on it directly; Flow streams the swarm along it (neighbor physics)</span>
      </Section>

      <Section
        title="Cymatics"
        summary={source.kind === "cymatics" ? `${cym.m}:${cym.n}${cym.mix === 1 ? "+" : "−"}${sweepOn ? " ⇢" : ""}${audioSweepOn ? " ♪" : ""}${energySweepOn ? " ∿" : ""}` : undefined}
        open={openCym}
        onToggle={() => {
          const opening = !openCym;
          setOpenCym(opening);
          if (opening && source.kind !== "cymatics") applyCymatics();
        }}
      >
        <div className="panel-row cym-row">
          <span className="seg">
            <button className={"chip seg-btn" + (cym.form === "plate" ? " active" : "")} onClick={() => setCymParam({ form: "plate" })}>Plate</button>
            <button className={"chip seg-btn" + (cym.form === "sand" ? " active" : "")} onClick={() => setCymParam({ form: "sand" })}>Sand</button>
          </span>
          <span className="seg">
            <button className={"chip seg-btn" + (cym.mix === 1 ? " active" : "")} onClick={() => setCymParam({ mix: 1 })} title="symmetric modes">+</button>
            <button className={"chip seg-btn" + (cym.mix === -1 ? " active" : "")} onClick={() => setCymParam({ mix: -1 })} title="antisymmetric modes">−</button>
          </span>
          {source.kind !== "cymatics" && <button className="chip" onClick={applyCymatics}>◈ Show plate</button>}
        </div>
        <div className="panel-row dials liss-dials">
          <NumberField label="Mode m" value={cym.m} min={1} max={10} step={1} onChange={(v) => setCymParam({ m: v })} />
          <NumberField label="Mode n" value={cym.n} min={1} max={10} step={1} onChange={(v) => setCymParam({ n: v })} />
          <NumberField label="Amplitude" value={cymAmp} min={0} max={2} step={0.05} onChange={setCymAmp} format={(v) => v.toFixed(2)} />
          <NumberField label="Plate size" value={cym.span} min={0.5} max={5} step={0.25} onChange={(v) => setCymParam({ span: v }, false)} format={(v) => v.toFixed(2) + "× nodes"} />
        </div>
        <div className="panel-row cym-row">
          <button className={"chip toggle" + (sweepOn ? " active" : "")} onClick={toggleSweep}>{sweepOn ? "■ Stop sweep" : "⇢ Frequency sweep"}</button>
          <button className={"chip toggle" + (audioSweepOn ? " active" : "")} onClick={toggleAudioSweep} title="Connected audio picks the figure — each kick climbs the mode ladder">
            {audioSweepOn ? "■ Beat sweep" : "♪ Beat sweep"}
          </button>
          <button className={"chip toggle" + (energySweepOn ? " active" : "")} onClick={toggleEnergySweep} title="Sustained loudness picks the pace — loud passages sweep, quiet ones hold the figure">
            {energySweepOn ? "■ Energy sweep" : "∿ Energy sweep"}
          </button>
          <NumberField label="Rate" value={sweepRate} min={0.2} max={8} step={0.1} onChange={setSweepRate} format={(v) => v.toFixed(1) + "/s"} />
          <NumberField label="From" value={sweepLo} min={1} max={ladderLength} step={1} onChange={setSweepLo} />
          <NumberField label="To" value={sweepHi} min={1} max={ladderLength} step={1} onChange={setSweepHi} />
        </div>
        <span className="phys-note">a Chladni plate — sweep climbs the mode ladder; Beat sweep lets the track pick the figure; Sand gathers on the nodal lines</span>
      </Section>

      <Section
        title="Visualizer"
        summary={visStyle ? visStyle : undefined}
        open={openVis}
        onToggle={() => {
          const opening = !openVis;
          setOpenVis(opening);
          if (opening && !visStyle) applyVisualizer("corona");
        }}
      >
        <div className="panel-row formations">
          {VIS_STYLE_LIST.map(({ key, label, hint }) => (
            <button key={key} className={"chip" + (visStyle === key ? " active" : "")} onClick={() => applyVisualizer(key)} title={hint}>
              {label}
            </button>
          ))}
        </div>
        <span className="phys-note">the drones ARE the analyser — connect audio and fly through it; silence relaxes to a rest shape</span>
      </Section>

      <Section
        title="Text & Image"
        summary={source.kind === "text" ? `"${source.text}"` : source.kind === "svg" || source.kind === "image" ? source.name : undefined}
        open={openText}
        onToggle={() => setOpenText((o) => !o)}
      >
        <div className="panel-row source-row">
          <form className="text-form" onSubmit={(e) => { e.preventDefault(); spell(); }}>
            <input
              className="text-input"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Type text…"
              maxLength={24}
            />
            <button type="submit" className="chip go">Spell</button>
          </form>
          <select className="font-select" value={font} onChange={onFontChange} title="Font" style={{ fontFamily: `"${font}"` }}>
            {fontList.map((f) => <option key={f} value={f} style={{ fontFamily: `"${f}"` }}>{f}</option>)}
          </select>
          <button className="chip as-file" onClick={loadSystemFonts} title="Load your installed system fonts">Fonts+</button>
          <label className="chip as-file">SVG…<input type="file" accept=".svg,image/svg+xml" hidden onChange={onSvgFile} /></label>
          <label className="chip as-file">Image…<input type="file" accept="image/*" hidden onChange={onImgFile} /></label>
          <span className="seg">
            <button className={"chip seg-btn" + (renderMode === "outline" ? " active" : "")} onClick={() => setRenderMode("outline")}>Outline</button>
            <button className={"chip seg-btn" + (renderMode === "fill" ? " active" : "")} onClick={() => setRenderMode("fill")}>Fill</button>
          </span>
        </div>
      </Section>

      <Section
        title="Color"
        summary={colorMode === "source" ? "Rainbow" : colorMode === "solid" ? "Solid" : "Gradient"}
        open={openColor}
        onToggle={() => setOpenColor((o) => !o)}
      >
        <div className="panel-row color-row">
          <span className="seg">
            <button className={"chip seg-btn" + (colorMode === "source" ? " active" : "")} onClick={() => setColorMode("source")}>Rainbow</button>
            <button className={"chip seg-btn" + (colorMode === "solid" ? " active" : "")} onClick={() => setColorMode("solid")}>Solid</button>
            <button className={"chip seg-btn" + (colorMode === "gradient" ? " active" : "")} onClick={() => setColorMode("gradient")}>Gradient</button>
          </span>
        </div>
        {colorMode === "solid" && (
          <div className="panel-row color-row">
            <label className="color-swatch" title="Pick a solid color" style={{ background: solidColor }}>
              <input type="color" value={solidColor} onChange={(e) => pickSolid(e.target.value)} />
            </label>
            {COLOR_PRESETS.map((hex) => (
              <button
                key={hex}
                className={"color-preset" + (solidColor.toLowerCase() === hex ? " active" : "")}
                style={{ background: hex }}
                title={hex}
                onClick={() => pickSolid(hex)}
              />
            ))}
          </div>
        )}
        {colorMode === "gradient" && (
          <GradientEditor stops={gradient} onChange={setGradient} axis={gradientAxis} onAxis={setGradientAxis} />
        )}
        <div className="panel-row color-row">
          <button className={"chip toggle" + (bloom ? " active" : "")} onClick={() => setBloom((b) => !b)}>Bloom</button>
          {bloom && <NumberField label="Bloom" value={bloomStrength} min={0} max={3} step={0.05} onChange={setBloomStrength} format={(v) => v.toFixed(2)} />}
          <NumberField label="Glow" value={brightness} min={0.6} max={2.6} step={0.05} onChange={(v) => setBrightness(v)} format={(v) => v.toFixed(2)} />
        </div>
      </Section>

      <Section
        title="Audio"
        summary={audio.source !== "none" ? "● live" : undefined}
        open={openAudio}
        onToggle={() => setOpenAudio((o) => !o)}
      >
        <AudioControls audio={audio} count={count} />
      </Section>

      <div className="panel-row load-row">
        <button className="chip load" onClick={loadSample} disabled={loading}>{loading ? "Loading…" : "▶ Load real show (MultiGP IO 2026)"}</button>
        <label className="chip load as-file">Open .zip…<input type="file" accept=".zip" hidden onChange={onShowFile} /></label>
        {error && <span className="err">{error}</span>}
        <span className="panel-hint">drag to orbit · scroll to zoom</span>
      </div>
    </>
  );
}
